<template>
	<component
		:is="assetCardComponent.component"
		v-bind="assetCardComponent.props"
		:class="[
			'display-block asset-element-card module module--on-white-bg',
			{ 'pointer-events-none': readOnlyAsset }
		]"
	>
		<AssetElementCardMobile
			v-if="isMobileView"
			:asset-element="assetElementViewModel"
			class="asset-element-card-mobile"
			aria-label="Asset Card"
		/>
		<AssetElementCardDesktop
			v-else
			:asset-element="assetElementViewModel"
			:show-chevron="!readOnlyAsset"
			class="asset-element-card-desktop"
			aria-label="Asset Card"
		/>
	</component>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import { Asset } from 'types/assets';
import AssetElementCardDesktop from './asset-element-card-desktop.vue';
import AssetElementCardMobile from './asset-element-card-mobile.vue';
import BaseLink from '@components/_base-link.vue';
import { DynamicComponent } from 'types/vue';
import { Link } from 'types/layout';
import { useAppStore } from '@stores/app';
import type { Component } from 'vue';

interface Props {
	assetElement: Asset;
	useMobileCard?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	useMobileCard: false
});

const appStore = useAppStore();

const readOnlyAsset = inject('readOnlyAssets', ref(false));

const assetCardComponent = computed((): DynamicComponent => {
	if (readOnlyAsset.value) {
		return {
			component: 'div' as unknown as Component
		};
	} else {
		return {
			component: BaseLink,
			props: {
				link: routerLink.value,
				classes: 'display-block asset-element-card module module--on-white-bg'
			}
		};
	}
});

const name = computed((): string => props.assetElement.name ?? props.assetElement.assetType);

const isMobileView = computed((): boolean => props.useMobileCard || appStore.isMobile);

const assetElementViewModel = computed(
	(): Asset => ({
		...props.assetElement,
		name: name.value
	})
);

const routerLink = computed((): Link => {
	switch (props.assetElement.elementType) {
		case 'REAL_ESTATE': {
			return {
				router: 'real-estate-assets',
				params: { reitElementId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'VENTURE': {
			return {
				router: 'company-assets',
				params: { companyId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'VENTURE_FUND': {
			return {
				router: 'venture-fund-assets',
				params: { ventureFundId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		case 'BOND': {
			return {
				router: 'bond-assets',
				params: { bondId: props.assetElement.fundriseId },
				forceHrefLink: appStore.isMobileWebview
			};
		}
		default:
			return { router: '404', forceHrefLink: appStore.isMobileWebview };
	}
});
</script>
<style lang="scss">
@use '../../styles/constants/_colors.scss' as *;

.asset-element-card {
	color: token('content-primary');
	border-radius: 0.5rem;
	margin-top: 0 !important;

	&:hover {
		color: token('content-primary');
		background: token('bg-secondary');
		border: 1px solid token('content-primary');
		cursor: pointer;
	}
}
</style>
